import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MessageCircle, X, Send, LifeBuoy, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';

const STORAGE_PREFIX = 'tt_support_chat_v1::';
const MAX_HISTORY = 40;

const initialAssistant = {
  role: 'assistant',
  content:
    "Hi, I'm TutorTracer's support assistant. Ask me anything about connecting integrations, finding features, troubleshooting, or how the platform works. If I can't help, tap \"Talk to a human\" at the top.",
};

const SupportChat = () => {
  const { user, organizationId } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [open, setOpen] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [messages, setMessages] = useState([initialAssistant]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [submittingTicket, setSubmittingTicket] = useState(false);
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketDescription, setTicketDescription] = useState('');
  const scrollRef = useRef(null);

  const storageKey = useMemo(
    () => (user?.id ? `${STORAGE_PREFIX}${user.id}` : null),
    [user?.id]
  );

  // Hydrate from sessionStorage once we know the user id.
  useEffect(() => {
    if (!storageKey) return;
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) setMessages(parsed);
      }
    } catch (e) {
      // sessionStorage may be unavailable in private mode; degrade silently.
    }
  }, [storageKey]);

  useEffect(() => {
    if (!storageKey) return;
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(messages.slice(-MAX_HISTORY)));
    } catch (e) {
      // ignore quota errors
    }
  }, [messages, storageKey]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, 30);
    return () => clearTimeout(t);
  }, [open, messages, sending, escalating]);

  if (!user) return null;

  const sendMessage = async () => {
    const trimmed = input.trim();
    if (!trimmed || sending) return;

    const next = [...messages, { role: 'user', content: trimmed }];
    setMessages(next);
    setInput('');
    setSending(true);

    try {
      // Strip the seeded greeting before sending — the system prompt sets tone.
      const payloadMessages = next
        .filter((m) => m !== initialAssistant)
        .slice(-MAX_HISTORY)
        .map((m) => ({ role: m.role, content: m.content }));

      const { data, error } = await supabase.functions.invoke('support-chat', {
        body: {
          messages: payloadMessages,
          organization_id: organizationId,
        },
      });
      if (error) throw error;
      const reply = data?.reply || "Sorry, I couldn't generate a response. Try again or talk to a human.";
      setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
    } catch (e) {
      console.error('support-chat error:', e);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content:
            "I hit an error talking to the AI. You can retry, or click \"Talk to a human\" at the top to escalate this directly to our team.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const submitTicket = async () => {
    const subj = ticketSubject.trim();
    const desc = ticketDescription.trim();
    if (!subj || !desc) {
      toast.error('Subject and description are required');
      return;
    }
    setSubmittingTicket(true);
    try {
      const { error } = await supabase.from('support_tickets').insert({
        org_id: organizationId,
        user_id: user.id,
        subject: subj,
        description: desc,
        transcript: messages,
        status: 'open',
      });
      if (error) throw error;

      // Fire-and-notify the edge function so it can email support. Failure here
      // doesn't block the user — the ticket row is already created.
      supabase.functions
        .invoke('support-chat', {
          body: {
            action: 'notify_ticket',
            organization_id: organizationId,
            subject: subj,
            description: desc,
            transcript: messages,
          },
        })
        .catch((e) => console.warn('ticket notify failed:', e));

      toast.success("Ticket submitted. We'll be in touch shortly.");
      setEscalating(false);
      setTicketSubject('');
      setTicketDescription('');
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: `Your ticket "${subj}" has been submitted. Our team will respond by email.`,
        },
      ]);
    } catch (e) {
      console.error('submit ticket error:', e);
      toast.error('Could not submit ticket. Please email support@tutortracer.com.');
    } finally {
      setSubmittingTicket(false);
    }
  };

  // ---------- styles ----------
  const panelBg = isDark ? 'bg-[#0d0d0f] border-[#27272a]' : 'bg-white border-[#e5e5e5]';
  const headerBg = isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-[#fafafa] border-[#e5e5e5]';
  const textMuted = isDark ? 'text-[#a1a1aa]' : 'text-[#666]';
  const textStrong = isDark ? 'text-white' : 'text-[#1a1a1a]';
  const inputClass = isDark
    ? 'bg-[#18181b] border-[#27272a] text-white placeholder:text-[#52525b]'
    : 'bg-white border-[#e5e5e5] text-[#1a1a1a] placeholder:text-[#999]';
  const accent = isDark ? 'text-[#7662ac]' : 'text-[#586f8d]';

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          aria-label="Open support chat"
          onClick={() => setOpen(true)}
          className={`fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-105 ${
            isDark ? 'bg-[#7662ac] text-white' : 'bg-[#586f8d] text-white'
          }`}
        >
          <MessageCircle className="h-6 w-6" strokeWidth={1.75} />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          role="dialog"
          aria-label="TutorTracer support chat"
          className={`fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-3rem)] rounded-xl border shadow-2xl flex flex-col overflow-hidden ${panelBg}`}
        >
          {/* Header */}
          <div className={`flex items-center justify-between px-4 py-3 border-b ${headerBg}`}>
            <div className="flex items-center gap-2">
              <div className={`h-7 w-7 rounded-full flex items-center justify-center ${isDark ? 'bg-[#18181b]' : 'bg-[#e8e8e8]'}`}>
                <MessageCircle className={`h-4 w-4 ${accent}`} strokeWidth={1.75} />
              </div>
              <div className="leading-tight">
                <div className={`text-sm font-medium ${textStrong}`}>Support</div>
                <div className={`text-[11px] ${textMuted}`}>AI assistant · ~instant</div>
              </div>
            </div>
            <button
              aria-label="Close support chat"
              onClick={() => setOpen(false)}
              className={`p-1 rounded hover:bg-black/5 dark:hover:bg-white/5 ${textMuted}`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Talk-to-human bar (always visible) */}
          <div className={`px-4 py-2 border-b ${headerBg}`}>
            <button
              onClick={() => setEscalating((v) => !v)}
              className={`w-full inline-flex items-center justify-center gap-2 text-xs font-medium py-2 rounded-md border transition-colors ${
                isDark
                  ? 'border-[#27272a] text-[#e4e4e7] hover:bg-[#18181b]'
                  : 'border-[#e5e5e5] text-[#1a1a1a] hover:bg-white'
              }`}
            >
              <LifeBuoy className="h-3.5 w-3.5" strokeWidth={1.75} />
              {escalating ? 'Back to chat' : 'Talk to a human'}
            </button>
          </div>

          {escalating ? (
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              <div>
                <label className={`block text-xs font-medium mb-1 ${textMuted}`}>Subject</label>
                <input
                  type="text"
                  value={ticketSubject}
                  onChange={(e) => setTicketSubject(e.target.value)}
                  placeholder="Brief summary of your issue"
                  className={`w-full px-3 py-2 text-sm rounded-md border outline-none focus:ring-1 focus:ring-[#7662ac] ${inputClass}`}
                />
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${textMuted}`}>Description</label>
                <textarea
                  value={ticketDescription}
                  onChange={(e) => setTicketDescription(e.target.value)}
                  placeholder="Tell us what's happening, what you've tried, and what you expected."
                  rows={6}
                  className={`w-full px-3 py-2 text-sm rounded-md border outline-none focus:ring-1 focus:ring-[#7662ac] resize-none ${inputClass}`}
                />
              </div>
              <p className={`text-[11px] ${textMuted}`}>
                Your current chat transcript will be attached automatically.
              </p>
              <button
                onClick={submitTicket}
                disabled={submittingTicket}
                className={`w-full inline-flex items-center justify-center gap-2 text-sm font-medium py-2 rounded-md transition-colors disabled:opacity-50 ${
                  isDark ? 'bg-[#7662ac] hover:bg-[#6b5a9d] text-white' : 'bg-[#586f8d] hover:bg-[#4a5e78] text-white'
                }`}
              >
                {submittingTicket && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Submit ticket
              </button>
            </div>
          ) : (
            <>
              {/* Messages */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
                {messages.map((m, i) => (
                  <div
                    key={i}
                    className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] px-3 py-2 rounded-lg text-sm leading-relaxed whitespace-pre-wrap ${
                        m.role === 'user'
                          ? isDark
                            ? 'bg-[#7662ac] text-white rounded-br-sm'
                            : 'bg-[#586f8d] text-white rounded-br-sm'
                          : isDark
                          ? 'bg-[#18181b] text-[#e4e4e7] rounded-bl-sm'
                          : 'bg-[#f5f5f5] text-[#1a1a1a] rounded-bl-sm'
                      }`}
                    >
                      {m.content}
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex justify-start">
                    <div
                      className={`px-3 py-2 rounded-lg text-sm inline-flex items-center gap-2 ${
                        isDark ? 'bg-[#18181b] text-[#a1a1aa]' : 'bg-[#f5f5f5] text-[#666]'
                      }`}
                    >
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Thinking…
                    </div>
                  </div>
                )}
              </div>

              {/* Composer */}
              <div className={`px-3 py-3 border-t ${headerBg}`}>
                <div className="flex items-end gap-2">
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask a question…"
                    rows={1}
                    className={`flex-1 px-3 py-2 text-sm rounded-md border outline-none focus:ring-1 focus:ring-[#7662ac] resize-none max-h-32 ${inputClass}`}
                  />
                  <button
                    onClick={sendMessage}
                    disabled={sending || !input.trim()}
                    aria-label="Send message"
                    className={`h-9 w-9 flex items-center justify-center rounded-md transition-colors disabled:opacity-40 ${
                      isDark ? 'bg-[#7662ac] hover:bg-[#6b5a9d] text-white' : 'bg-[#586f8d] hover:bg-[#4a5e78] text-white'
                    }`}
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default SupportChat;
